import React, { useCallback, useEffect, useState } from 'react';
import {
  AccountBalanceWallet,
  ArrowDownward,
  ArrowUpward,
  ReceiptLongOutlined,
} from '@mui/icons-material';
import comms from '../comms';

// Transactions/Ledger screen (docs/DriverRequirement/05_cashiering_and_financials
// (1).md, 08_driverapp_cashiering_handoff.md): a single chronological bank-ledger
// list backed by POST /API/Driver/Ledger — date/time, type+description, money
// in/out via `direction`, absolute amount, runningBalance, and pending/settled
// status. No Deposits/Payments/Today/History tabs.
function fmtMoney(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isNaN(n) ? String(v) : `$${n.toFixed(2)}`;
}

function fmtWhen(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function TransactionsTabs({ userSettings }) {
  const driverId =
    userSettings?.Id || userSettings?.id || JSON.parse(localStorage.getItem('userSettings') || '{}')?.Id;

  const [state, setState] = useState({ status: 'loading', balance: null, entries: [] });

  const load = useCallback(async () => {
    if (!driverId) {
      setState({ status: 'error', balance: null, entries: [] });
      return;
    }
    setState((prev) => ({ ...prev, status: 'loading' }));
    try {
      const res = await comms.getDriverLedger(driverId);
      if (!res || res.status === false) {
        setState({ status: 'error', balance: null, entries: [], message: res?.message });
        return;
      }
      setState({
        status: 'loaded',
        balance: Number(res.balance) || 0,
        entries: Array.isArray(res.entries) ? res.entries : [],
      });
    } catch (err) {
      setState({ status: 'error', balance: null, entries: [], message: err?.message });
    }
  }, [driverId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-gradient-to-br from-blue-800 to-blue-600 px-5 py-5 text-white shadow-sm">
        <div className="flex items-center gap-2 text-blue-100">
          <AccountBalanceWallet style={{ fontSize: 20 }} />
          <span className="text-sm font-medium">Balance</span>
        </div>
        <div className="mt-3 text-3xl font-bold tracking-tight">
          {state.status === 'loaded' ? fmtMoney(state.balance) ?? '$0.00' : '—'}
        </div>
      </div>

      {state.status === 'loading' && (
        <div className="space-y-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-16 animate-pulse rounded-2xl bg-gray-200" />
          ))}
        </div>
      )}

      {state.status === 'error' && (
        <div className="rounded-2xl bg-white px-4 py-6 text-center text-sm text-gray-500 shadow-sm">
          {state.message || 'Unable to load transactions.'}
        </div>
      )}

      {state.status === 'loaded' && !state.entries.length && (
        <div className="rounded-2xl bg-white px-4 py-8 text-center text-sm text-gray-500 shadow-sm">
          No transactions found.
        </div>
      )}

      {state.status === 'loaded' && state.entries.length > 0 && (
        <div className="space-y-2">
          {state.entries.map((entry, i) => {
            const isIn = entry.direction === 'in';
            const isPending = entry.status === 'pending';
            return (
              <div key={entry.id ?? i} className="flex items-center gap-3 rounded-2xl bg-white px-4 py-3 shadow-sm">
                <div
                  className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full"
                  style={isIn ? { background: '#ECFDF5' } : { background: '#FEF2F2' }}
                >
                  {isIn ? (
                    <ArrowDownward style={{ fontSize: 20, color: '#059669' }} />
                  ) : (
                    <ArrowUpward style={{ fontSize: 20, color: '#DC2626' }} />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm text-gray-800">
                      {entry.description || entry.type || 'Transaction'}
                    </span>
                    <span
                      className="flex-shrink-0 text-sm font-semibold"
                      style={{ color: isIn ? '#059669' : '#DC2626' }}
                    >
                      {isIn ? '+' : '-'}
                      {fmtMoney(Math.abs(Number(entry.amount) || 0))}
                    </span>
                  </div>
                  <div className="mt-0.5 flex items-center justify-between gap-2 text-xs text-gray-500">
                    <span>{fmtWhen(entry.date)}</span>
                    <span>Balance: {fmtMoney(entry.runningBalance) ?? '—'}</span>
                  </div>
                  <span
                    className="mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold"
                    style={
                      isPending
                        ? { background: '#FFFBEB', color: '#B45309' }
                        : { background: '#ECFDF5', color: '#059669' }
                    }
                  >
                    <ReceiptLongOutlined style={{ fontSize: 12 }} />
                    {isPending ? 'Pending' : 'Settled'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default TransactionsTabs;
